import { useState } from 'react';
import { CronoAttachment } from 'crono-fe-common/types/cronoAttachment';
import { Template } from 'crono-fe-common/types/template';
import { FileContent } from 'use-file-picker';
import { isFileContent } from 'pages/templates/model';
import { formatHtmlContent } from 'utils/formatUtils';
import { formatMissingVariableBrackets } from 'crono-fe-common/utils';
import useCreateTemplate from 'hooks/services/templates/useCreateTemplate';
import usePatchTemplate from 'hooks/services/templates/usePatchTemplate';
import useAddTemplateAttachment from 'hooks/services/templates/useAddTemplateAttachment';
import useDeleteTemplateAttachment from 'hooks/services/templates/useDeleteTemplateAttachment';
import usePatchStrategyStep from 'hooks/services/strategy/usePatchStrategyStep';
import usePatchConditionalStrategyStep from 'hooks/services/strategy/usePatchConditionalStrategyStep';
import useAddStrategyStepTemplate from 'hooks/services/strategy/useAddStrategyStepTemplate';
import useDeleteStrategyStepTemplate from 'hooks/services/strategy/useDeleteStrategyStepTemplate';
import useGetStrategy, {
  useInvalidateGetStrategy,
} from 'hooks/services/strategy/useGetStrategy';
import { StrategyWithAnalytics } from 'crono-fe-common/types/strategy';
import { Constants } from 'crono-fe-common/constants/constants';
import { useAnalytics } from 'context/analytics';
import {
  buildSingleUseTemplateTitle,
  buildSingleUseTemplateTitleFromContent,
} from 'context/strategyDetail/singleUseTemplate';

interface UseSaveStepOnlyTemplateParams {
  stepId: number;
  conditionalStepId?: number;
  isABTesting: boolean;
  isConditional: boolean;
  strategyId?: number;
  onSuccess?: (
    data:
      | {
          newInsertedTemplateId: number;
          isAbTesting: boolean;
        }
      | undefined,
  ) => void;
}

interface SaveStepOnlyTemplateInput {
  template: Template;
  subject: string;
  content: string;
  attachments: (FileContent | CronoAttachment)[];
  initialAttachments: CronoAttachment[];
}

export const useSaveStepOnlyTemplate = ({
  stepId,
  conditionalStepId,
  isABTesting,
  isConditional,
  strategyId,
  onSuccess,
}: UseSaveStepOnlyTemplateParams) => {
  const analytics = useAnalytics();

  //Covers the whole save, not just the create call: the step patch and the
  //attachment sync that follow it are what a second click would duplicate
  const [isSaving, setIsSaving] = useState(false);

  const { data: strategyResponse } = useGetStrategy(strategyId);
  const strategy =
    strategyResponse?.data.data ?? ({ id: -1 } as StrategyWithAnalytics);

  const invalidateGetStrategy = useInvalidateGetStrategy();

  const { mutateAsync: createTemplate } = useCreateTemplate();
  const { mutateAsync: patchTemplate } = usePatchTemplate();
  const { mutateAsync: addTemplateAttachment } = useAddTemplateAttachment();
  const { mutateAsync: deleteTemplateAttachment } =
    useDeleteTemplateAttachment();
  const { mutateAsync: patchStrategyStep } = usePatchStrategyStep();
  const { mutateAsync: patchConditionalStrategyStep } =
    usePatchConditionalStrategyStep();
  const { mutateAsync: addStrategyStepTemplate } = useAddStrategyStepTemplate();
  const { mutateAsync: deleteStrategyStepTemplate } =
    useDeleteStrategyStepTemplate();

  const syncAttachments = async (
    templateId: number,
    attachments: (FileContent | CronoAttachment)[],
    initialAttachments: CronoAttachment[],
  ) => {
    const keptAttachmentIds = attachments
      .filter((attachment) => !isFileContent(attachment))
      .map((attachment) => (attachment as CronoAttachment).id);

    const attachmentsToDelete = initialAttachments.filter(
      (attachment) => !keptAttachmentIds.includes(attachment.id),
    );

    await Promise.all(
      attachmentsToDelete.map((attachment) =>
        deleteTemplateAttachment({ templateId, attachmentId: attachment.id }),
      ),
    );

    const attachmentsToAdd = attachments.filter(isFileContent);
    if (attachmentsToAdd.length > 0) {
      await addTemplateAttachment({
        templateId,
        attachments: attachmentsToAdd,
      });
    }
  };

  const findStrategyStepTemplateId = (templateId: number) => {
    if (isConditional && conditionalStepId) {
      return strategy.steps
        ?.find((step) => step.strategyStepConditional?.id === conditionalStepId)
        ?.strategyStepConditional?.strategyStepTemplates?.find(
          (strategyStepTemplate) =>
            strategyStepTemplate.template?.id === templateId,
        )?.id;
    }
    return strategy.steps
      ?.find((step) => step.id === stepId)
      ?.strategyStepTemplates?.find(
        (strategyStepTemplate) =>
          strategyStepTemplate.template?.id === templateId,
      )?.id;
  };

  const attachTemplateToStep = async (
    templateId: number,
    originalTemplate: Template,
  ) => {
    if (isABTesting) {
      const responseAdd = await addStrategyStepTemplate({
        strategyStepId: stepId,
        ...(isConditional && conditionalStepId
          ? { strategyStepConditionalId: conditionalStepId }
          : {}),
        templateId,
        templateType: originalTemplate.type,
      });

      if (responseAdd?.data?.data) {
        const strategyStepTemplateId = findStrategyStepTemplateId(
          originalTemplate.id,
        );
        if (strategyStepTemplateId) {
          await deleteStrategyStepTemplate({ strategyStepTemplateId });
        }
      }
      return;
    }

    if (isConditional) {
      const step = strategy.steps?.find((step) => step.id === stepId);
      if (step?.strategyStepConditional) {
        await patchConditionalStrategyStep({
          strategyStepId: stepId,
          templateId,
        });
      }
      return;
    }

    await patchStrategyStep({
      id: stepId,
      strategyId: strategy.id,
      templateId,
    });
  };

  const saveStepOnlyTemplate = async (input: SaveStepOnlyTemplateInput) => {
    setIsSaving(true);
    try {
      await runSaveStepOnlyTemplate(input);
    } finally {
      setIsSaving(false);
    }
  };

  const runSaveStepOnlyTemplate = async ({
    template,
    subject,
    content,
    attachments,
    initialAttachments,
  }: SaveStepOnlyTemplateInput) => {
    const formattedContent = formatHtmlContent(content);
    const formattedSubject = formatMissingVariableBrackets(subject);

    // Re-saving a template that already belongs to this step only: keep the
    // same template so we don't pile up "Copy Copy" duplicates.
    if (template.isSingleUse) {
      await patchTemplate({
        id: template.id,
        content: formattedContent,
        subject: formattedSubject,
      });
      await syncAttachments(template.id, attachments, initialAttachments);

      invalidateGetStrategy({ strategyId: strategy.id });
      onSuccess?.(undefined);
      return;
    }

    const existingAttachments = attachments.filter(
      (attachment) => !isFileContent(attachment),
    ) as CronoAttachment[];
    const newAttachments = attachments.filter(isFileContent);

    const { data: response } = await createTemplate({
      ...template,
      title: template.title
        ? buildSingleUseTemplateTitle(template.title)
        : buildSingleUseTemplateTitleFromContent(formattedContent),
      subject: formattedSubject,
      content: formattedContent,
      language: template.language || Constants.defaultLanguage,
      shared: template.shared ?? true,
      isSingleUse: true,
      attachments: newAttachments,
      attachmentIds: existingAttachments.map((attachment) => attachment.id),
    });

    analytics?.track('create-template', {
      fromCustomization: true,
      singleUse: true,
    });

    const createdTemplateId = response?.data?.id;
    if (!createdTemplateId) return;

    await attachTemplateToStep(createdTemplateId, template);

    invalidateGetStrategy({ strategyId: strategy.id });
    onSuccess?.({
      newInsertedTemplateId: createdTemplateId,
      isAbTesting: isABTesting,
    });
  };

  return {
    saveStepOnlyTemplate,
    isSaveStepOnlyTemplateLoading: isSaving,
  };
};
